import { useEffect, useId, useRef, useState } from "react";

import FormFieldStatus from "./FormFieldStatus";

// Regex from database constraint: must be # followed by 3 or 6 hex digits
const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function isValidHexColor(value: string): boolean {
  return HEX_COLOR_REGEX.test(value);
}

export interface ColorPickerFieldProps {
  /** The current color value (hex format) */
  value: string;
  /** Callback when the color changes */
  onChange: (value: string) => void;
  /** Title/label for the color picker */
  title: string;
  /** Description text below the title */
  description?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Show the hex value next to the input */
  showHexValue?: boolean;
  /** Loading state for feedback */
  loading?: boolean;
  /** Loading message */
  loadingMessage?: string;
  /** Success state for feedback */
  success?: boolean;
  /** Success message */
  successMessage?: string;
  /** Error state for feedback */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
  /** Debounce delay in ms for hex input (default: 500) */
  debounceMs?: number;
}

export default function ColorPickerField({
  value,
  onChange,
  title,
  description,
  disabled = false,
  showHexValue = true,
  loading = false,
  loadingMessage,
  success = false,
  successMessage,
  error = false,
  errorMessage,
  debounceMs = 500,
}: ColorPickerFieldProps) {
  const id = useId();
  const [localHexValue, setLocalHexValue] = useState(value);
  const [validationError, setValidationError] = useState<string | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Sync local value when prop changes (e.g., from color picker or external update)
  useEffect(() => {
    setLocalHexValue(value);
    setValidationError(null);
  }, [value]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleHexInputChange = (inputValue: string) => {
    // Allow typing without the hash prefix
    let hex = inputValue.startsWith("#") ? inputValue : `#${inputValue}`;
    // Limit to 7 characters (#RRGGBB)
    hex = hex.slice(0, 7);

    // Only update local state if it looks like a hex pattern (allow partial input while typing)
    if (/^#[0-9A-Fa-f]*$/.test(hex)) {
      setLocalHexValue(hex);
      setValidationError(null);

      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Only trigger onChange when we have a complete valid hex color
      if (isValidHexColor(hex)) {
        debounceTimer.current = setTimeout(() => {
          onChange(hex);
        }, debounceMs);
      } else if (hex.length >= 4) {
        // Show validation error after user has typed enough characters
        debounceTimer.current = setTimeout(() => {
          setValidationError(
            "Hex code must be # followed by 3 or 6 digits (e.g., #FFF or #FFFFFF)"
          );
        }, debounceMs);
      }
    }
  };

  // Determine which status to show: validation error takes precedence over external states
  const showValidationError = validationError !== null && !loading;
  const showExternalError = error && !showValidationError;
  const showSuccess = success && !showValidationError && !loading;

  return (
    <div className="flex flex-col gap-y-2.5">
      <div className="flex flex-col gap-1">
        <span className="text-base font-medium text-primary-blue">{title}</span>
        {description && (
          <p className="text-sm text-secondary-blue">{description}</p>
        )}
      </div>
      <div className="flex flex-row items-center gap-x-3">
        <div className="relative overflow-hidden w-8 h-8 border-2 border-gray-100 rounded-md">
          <input
            id={id}
            type="color"
            className="absolute -top-2 -right-2 w-12 h-12 border-none cursor-pointer"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
        </div>
        {showHexValue && (
          <input
            type="text"
            className="text-sm text-secondary-blue font-mono bg-transparent border-none outline-none w-20 cursor-text hover:text-primary-blue focus:text-primary-blue"
            value={localHexValue}
            onChange={(e) => handleHexInputChange(e.target.value)}
            disabled={disabled}
            maxLength={7}
            spellCheck={false}
            autoComplete="off"
            aria-label={`${title} hex value`}
          />
        )}
      </div>
      <FormFieldStatus
        loading={loading}
        loadingMessage={loadingMessage}
        success={showSuccess}
        successMessage={successMessage}
        error={showValidationError || showExternalError}
        errorMessage={showValidationError ? validationError : errorMessage}
      />
    </div>
  );
}
